import React, { useState } from 'react';
import { DeliveryAddressSelected } from './DeliveryAddressSelected';
import {
  DELIVERY_ADDRESS_TYPE,
  DELIVERY_INSTRUCTION,
} from '../libs/polygon-ordering/src/constants/deliveryAddress';
import SectionHeading from './SectionHeading';
import SectionContainer from './SectionContainer';
import SubSection from './SubSection';
import { Pill } from './Pill';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import getThemeLookup from '../selectors/getThemeLookup';
import { TEXT_PROPERTIES } from '../utils/theme';
import { RadioToggle } from './RadioToggle';
import combineStyles from '../utils/combineStyles';
import { deleteSavedAddress } from '../libs/polygon-ordering/src/operations';
import { updateForm, resetForm, setSelectedAddress } from '../slices/deliveryAddressForm';
import { setDesiredDeliveryAddress } from '../slices/desiredDeliveryAddress';
import { useTranslation } from 'react-i18next';
import getUserLocation from '../selectors/getUserLocation';
import Map from './Map';
import RedcatDoorOpenIcon from './icons/redcat/RedcatDoorOpenIcon';
import RedcatGroupIcon from './icons/redcat/RedcatGroupIcon';
import RedcatPhoneMessageIcon from './icons/redcat/RedcatPhoneMessageIcon';
import RedcatMapPinHomeIcon from './icons/redcat/RedcatMapPinHomeIcon';
import RedcatBriefcaseIcon from './icons/redcat/RedcatBriefcaseIcon';
import RedcatMapPinGroupIcon from './icons/redcat/RedcatMapPinGroupIcon';
import RedcatMapPinVariantIcon from './icons/redcat/RedcatMapPinVariantIcon';
import { getMember } from '../libs/polygon-ordering/src/selectors';

interface SaveOption {
  value: string;
  onClick: () => void;
  isActive: boolean;
  icon: JSX.Element;
}

interface DeliveryDetailsConfirmationProps {
  onOpenAddressInput?: () => void;
}

export const DeliveryDetailsConfirmation = ({
  onOpenAddressInput,
}: DeliveryDetailsConfirmationProps) => {
  const dispatch = useAppDispatch();
  const p = useAppSelector(getThemeLookup);
  const { t } = useTranslation();
  const formState = useAppSelector(state => state.deliveryAddressForm);

  const userLocation = useAppSelector(getUserLocation);
  const markerPosition = formState.selectedAddress?.location || userLocation;

  const member = useAppSelector(getMember);

  const [removeAddressDialogOpen, setRemoveAddressDialogOpen] = useState(false);

  const iconColor = p('savedAddressIcon', ['color']).color;
  const activeIconColor = p('savedAddressIconActive', ['color']).color;

  const onDeliveryInstructionsClicked = (deliveryInstruction: DELIVERY_INSTRUCTION | undefined) => {
    if (!formState.selectedAddress) return;

    if (formState.selectedAddress.deliveryInstruction === deliveryInstruction) {
      deliveryInstruction = undefined;
    }

    dispatch(
      updateForm({
        selectedAddress: { ...formState.selectedAddress, deliveryInstruction },
      }),
    );
  };

  const onAddressTypeClicked = (addressType: DELIVERY_ADDRESS_TYPE) => {
    if (!formState.selectedAddress) {
      return;
    }

    const address: DeliveryAddress = {
      ...formState.selectedAddress,
      addressType,
    };

    if (!formState.editingAddress && formState.selectedAddress.addressType === addressType) {
      address.addressType = undefined;
      address.locationName = undefined;
    }

    if (addressType === DELIVERY_ADDRESS_TYPE.HOME || addressType === DELIVERY_ADDRESS_TYPE.WORK) {
      address.locationName = undefined;
    }

    dispatch(setSelectedAddress(address));
  };

  const onRemoveClicked = async () => {
    setRemoveAddressDialogOpen(true);
  };

  const onRemoveConfirmed = async () => {
    if (
      !formState.selectedAddress ||
      !formState.selectedAddress.addressId ||
      formState.submittingForm
    ) {
      return;
    }

    dispatch(updateForm({ submittingForm: true }));
    await dispatch(deleteSavedAddress({ id: formState.selectedAddress.addressId }));
    dispatch(setDesiredDeliveryAddress(null));
    dispatch(resetForm());
  };

  const saveOptions: SaveOption[] = [
    {
      value: t('deliveryAddress.addressTypeHome'),
      onClick: () => onAddressTypeClicked(DELIVERY_ADDRESS_TYPE.HOME),
      isActive: formState.selectedAddress?.addressType === DELIVERY_ADDRESS_TYPE.HOME,
      icon: (
        <RedcatMapPinHomeIcon
          size={16}
          color={
            formState.selectedAddress?.addressType == DELIVERY_ADDRESS_TYPE.HOME
              ? activeIconColor
              : iconColor
          }
        />
      ),
    },
    {
      value: t('deliveryAddress.addressTypeWork'),
      onClick: () => onAddressTypeClicked(DELIVERY_ADDRESS_TYPE.WORK),
      isActive: formState.selectedAddress?.addressType === DELIVERY_ADDRESS_TYPE.WORK,
      icon: (
        <RedcatBriefcaseIcon
          size={16}
          color={
            formState.selectedAddress?.addressType == DELIVERY_ADDRESS_TYPE.WORK
              ? activeIconColor
              : iconColor
          }
        />
      ),
    },
    {
      value: t('deliveryAddress.addressTypeFriendsFamily'),
      onClick: () => onAddressTypeClicked(DELIVERY_ADDRESS_TYPE.FRIENDS_FAMILY),
      isActive: formState.selectedAddress?.addressType === DELIVERY_ADDRESS_TYPE.FRIENDS_FAMILY,
      icon: (
        <RedcatMapPinGroupIcon
          size={16}
          color={
            formState.selectedAddress?.addressType == DELIVERY_ADDRESS_TYPE.FRIENDS_FAMILY
              ? activeIconColor
              : iconColor
          }
        />
      ),
    },
    {
      value: t('deliveryAddress.addressTypeOther'),
      onClick: () => onAddressTypeClicked(DELIVERY_ADDRESS_TYPE.OTHER),
      isActive: formState.selectedAddress?.addressType === DELIVERY_ADDRESS_TYPE.OTHER,
      icon: (
        <RedcatMapPinVariantIcon
          size={16}
          color={
            formState.selectedAddress?.addressType == DELIVERY_ADDRESS_TYPE.OTHER
              ? activeIconColor
              : iconColor
          }
        />
      ),
    },
  ];

  const onSelectedAddressClicked = () => {
    onOpenAddressInput?.();
  };

  const onDeliveryNotesChanged = (value?: string) => {
    if (!formState.selectedAddress) return;
    dispatch(
      updateForm({
        selectedAddress: { ...formState.selectedAddress, deliveryNotes: value },
      }),
    );
  };

  const onLocationNameChanged = (value: string) => {
    if (!formState.selectedAddress) return;
    dispatch(
      updateForm({
        selectedAddress: { ...formState.selectedAddress, locationName: value },
      }),
    );
  };

  const onRemoveAddressDialogClosed = () => {
    if (formState.submittingForm) return;
    setRemoveAddressDialogOpen(false);
  };

  return (
    <>
      <div>
        <Map mapHeight={200} markerPosition={markerPosition} />
      </div>
      <div style={combineStyles(styles.selectedAddressContainer, styles.sideMargin)}>
        <DeliveryAddressSelected onClick={onSelectedAddressClicked} />
      </div>
      <SectionHeading
        title={t('deliveryAddress.deliveryInstructionsHeader')}
        containerStyle={styles.sidePadding}
      />
      <SectionContainer style={styles.sidePadding}>
        <SubSection
          subtitle={t('deliveryAddress.deliveryInstructionsLeaveAtMyDoor')}
          action={() => onDeliveryInstructionsClicked(DELIVERY_INSTRUCTION.LEAVE_AT_MY_DOOR)}
          leftIcon={() => (
            <RedcatDoorOpenIcon
              color={
                formState.selectedAddress?.deliveryInstruction ===
                DELIVERY_INSTRUCTION.LEAVE_AT_MY_DOOR
                  ? activeIconColor
                  : iconColor
              }
            />
          )}
          rightSlot={() => (
            <RadioToggle
              checked={
                formState.selectedAddress?.deliveryInstruction ===
                DELIVERY_INSTRUCTION.LEAVE_AT_MY_DOOR
              }
            />
          )}
          active={
            formState.selectedAddress?.deliveryInstruction === DELIVERY_INSTRUCTION.LEAVE_AT_MY_DOOR
          }
        ></SubSection>
        <SubSection
          subtitle={t('deliveryAddress.deliveryInstructionsHandItToMe')}
          action={() => onDeliveryInstructionsClicked(DELIVERY_INSTRUCTION.HAND_IT_TO_ME)}
          leftIcon={() => (
            <RedcatGroupIcon
              color={
                formState.selectedAddress?.deliveryInstruction ===
                DELIVERY_INSTRUCTION.HAND_IT_TO_ME
                  ? activeIconColor
                  : iconColor
              }
            />
          )}
          rightSlot={() => (
            <RadioToggle
              checked={
                formState.selectedAddress?.deliveryInstruction ===
                DELIVERY_INSTRUCTION.HAND_IT_TO_ME
              }
            />
          )}
          active={
            formState.selectedAddress?.deliveryInstruction === DELIVERY_INSTRUCTION.HAND_IT_TO_ME
          }
        ></SubSection>
        <SubSection
          subtitle={t('deliveryAddress.deliveryInstructionsContactOnArrival')}
          action={() => onDeliveryInstructionsClicked(DELIVERY_INSTRUCTION.CONTACT_ON_ARRIVAL)}
          leftIcon={() => (
            <RedcatPhoneMessageIcon
              color={
                formState.selectedAddress?.deliveryInstruction ===
                DELIVERY_INSTRUCTION.CONTACT_ON_ARRIVAL
                  ? activeIconColor
                  : iconColor
              }
            />
          )}
          rightSlot={() => (
            <RadioToggle
              checked={
                formState.selectedAddress?.deliveryInstruction ===
                DELIVERY_INSTRUCTION.CONTACT_ON_ARRIVAL
              }
            />
          )}
          active={
            formState.selectedAddress?.deliveryInstruction ===
            DELIVERY_INSTRUCTION.CONTACT_ON_ARRIVAL
          }
        ></SubSection>
      </SectionContainer>

      <SectionHeading
        title={t('deliveryAddress.deliveryNotesHeader')}
        containerStyle={styles.sidePadding}
      />
      <div style={combineStyles(styles.inputContainer, styles.sideMargin)}>
        <TextField
          multiline
          rows={3}
          value={formState.selectedAddress?.deliveryNotes || ''}
          onChange={event => onDeliveryNotesChanged(event.target.value || undefined)}
          fullWidth={true}
          inputProps={{ maxLength: 280 }}
        />
        <div style={{ ...p('textFieldCharacterCount', TEXT_PROPERTIES), ...styles.characterCount }}>
          {formState.selectedAddress?.deliveryNotes?.length || 0} / 280
        </div>
      </div>

      {member && (
        <>
          <SectionHeading
            title={t('deliveryAddress.saveAsHeader')}
            containerStyle={styles.sidePadding}
          />
          <div style={combineStyles(styles.pillContainer, styles.sideMargin)}>
            {saveOptions.map(option => (
              <Pill
                key={option.value}
                value={option.value}
                onClick={option.onClick}
                isActive={option.isActive}
                icon={option.icon}
              />
            ))}
          </div>

          {(formState.selectedAddress?.addressType === DELIVERY_ADDRESS_TYPE.FRIENDS_FAMILY ||
            formState.selectedAddress?.addressType === DELIVERY_ADDRESS_TYPE.OTHER) && (
            <div
              style={combineStyles(styles.inputContainer, styles.sideMargin, {
                marginBottom: 20,
              })}
            >
              <TextField
                value={formState.selectedAddress?.locationName || ''}
                onChange={event => onLocationNameChanged(event.target.value)}
                placeholder={t('deliveryAddress.locationNamePlaceholder')}
                fullWidth={true}
                inputProps={{ maxLength: 25 }}
              />
            </div>
          )}

          {formState.editingAddress && (
            <div style={styles.removeButtonContainer}>
              <span
                style={combineStyles(
                  styles.removeButton,
                  p('removeSavedAddressButton', TEXT_PROPERTIES),
                )}
                onClick={onRemoveClicked}
              >
                {t('deliveryAddress.removeThisSavedAddress')}
              </span>
            </div>
          )}

          <Dialog open={removeAddressDialogOpen} onClose={onRemoveAddressDialogClosed}>
            <DialogTitle>{t('deliveryAddress.removeAddressDialogHeader')}</DialogTitle>
            <DialogContent>
              <DialogContentText>{t('deliveryAddress.removeAddressDialogBody')}</DialogContentText>
              <DialogContentText style={{ marginTop: 10, fontWeight: '700' }}>
                {formState.selectedAddress?.formattedAddress}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setRemoveAddressDialogOpen(false)}
                style={p('removeAddressDialogCancel', TEXT_PROPERTIES)}
                disabled={formState.submittingForm}
              >
                {t('deliveryAddress.removeAddressDialogCancel')}
              </Button>
              <Button
                onClick={onRemoveConfirmed}
                style={p('removeAddressDialogConfirm', TEXT_PROPERTIES)}
                disabled={formState.submittingForm}
              >
                {t('deliveryAddress.removeAddressDialogConfirm')}
                {formState.submittingForm && <CircularProgress size={18} style={styles.spinner} />}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};

const styles: Styles = {
  sideMargin: {
    marginLeft: 16,
    marginRight: 16,
  },
  sidePadding: {
    paddingLeft: 16,
    paddingRight: 16,
  },
  selectedAddressContainer: {
    marginTop: 20,
    marginBottom: 20,
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  pillContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 5,
    marginBottom: 20,
  },
  characterCount: {
    textAlign: 'right',
    marginTop: 5,
  },
  removeButtonContainer: {
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    paddingBottom: 20,
  },
  removeButton: {
    cursor: 'pointer',
  },
  spinner: {
    marginLeft: 8,
  },
};
